// src/pages/Dashboard/components/YourAgreement.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. The salesperson half of the one on one.
//
// WHAT YOU AND YOUR MANAGER AGREED, AND WHEN THEY ARE COMING BACK TO LOOK.
//
// Jazz's one on one ends with a correction the manager demonstrates and a date
// to come back and inspect it. 0039 lets the salesperson read the record
// because "here is what we agreed and here is when I am coming back to look
// only works if they can see it", and until today there was nowhere they could.
// The coach even told them they could not.
//
// ONLY THE FORWARD HALF. What the manager showed them and the return date.
// The behaviour and its cost stay on the record, not on the screen somebody
// opens at 8:40 before their first up. See agreementFor in oneOnOneShape.js.
//
// It renders nothing at all when there is nothing to carry forward, which for
// almost everybody today is always.

import { useEffect, useState } from 'react'
import { Box, HStack, Text, Button } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Handshake } from 'lucide-react'
import { supabase } from '../../../lib/supabase'
import { getLatestForMe } from '../../../lib/oneOnOne'
import { agreementFor } from '../../../lib/oneOnOneShape'
import { firstNameFor } from '../../../lib/names.js'
import { dayKey, parseDay } from '../../../lib/localDate'

const fmt = (key) => parseDay(key).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })

export default function YourAgreement({ userId }) {
  const [agreement, setAgreement] = useState(null)
  const [manager, setManager] = useState(null)

  useEffect(() => {
    if (!userId) return
    let live = true
    ;(async () => {
      const { data } = await getLatestForMe(userId)
      const a = agreementFor(data, dayKey())
      if (!live || !a) return
      setAgreement(a)
      const { data: m } = await supabase
        .from('users')
        .select('id, display_name, first_name')
        .eq('id', a.managerId)
        .maybeSingle()
      if (live && m) setManager(m)
    })()
    return () => {
      live = false
    }
  }, [userId])

  return <YourAgreementView agreement={agreement} manager={manager} />
}

export function YourAgreementView({ agreement, manager }) {
  if (!agreement) return null
  const who = firstNameFor(manager) || 'Your manager'
  const back =
    agreement.due === 'today'
      ? `${who} is coming back to look today.`
      : agreement.due === 'upcoming'
        ? `${who} is coming back to look on ${fmt(agreement.inspectOn)}.`
        : agreement.due === 'overdue'
          ? `${who} said they would come back to look on ${fmt(agreement.inspectOn)}. Worth asking them.`
          : null

  return (
    <Box bg="card" border="1px solid" borderColor="line" borderRadius="card" px={{ base: 5, md: 6 }} py={5}>
      <HStack spacing={2} mb={2} color="inkDim">
        <Handshake size={14} aria-hidden="true" />
        <Text textStyle="eyebrow">WHAT YOU AND {who.toUpperCase()} AGREED</Text>
      </HStack>
      <Text fontSize="lg" fontWeight={600} letterSpacing="-0.01em" lineHeight={1.4} maxW="measure">
        {agreement.correction}
      </Text>
      <Text fontSize="body-sm" color="inkMuted" mt={2}>
        From your one on one on {fmt(agreement.heldOn)}.{back ? ` ${back}` : ''}
      </Text>
      <Button as={RouterLink} to="/coach/?mode=agreement" size="sm" variant="outline" mt={3}>
        Rehearse it with the coach
      </Button>
    </Box>
  )
}
